import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import {
  ConfirmDialog, EmptyState, ErrorState, FileUploader, FilterBar, FormDialog, PageHeader, type UploadedFile,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { useEstablishments } from '@/features/org'
import { toApiProblem } from '@/lib/api/client'
import { DocumentStatusBadge } from '../components/DocumentStatusBadge'
import { useCompanyDocumentMutations, useCompanyDocuments, useDocumentTypes } from '../api'
import type { CompanyDocumentView } from '../types'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

interface Draft {
  establishmentId: string
  documentTypeId: string
  documentNumber: string
  issuingAuthority: string
  issueDate: string
  expiryDate: string
  storedFileId: string | null
  notes: string
}

const EMPTY: Draft = {
  establishmentId: '',
  documentTypeId: '',
  documentNumber: '',
  issuingAuthority: '',
  issueDate: '',
  expiryDate: '',
  storedFileId: null,
  notes: '',
}

/**
 * Trade licences, establishment cards and the rest, per entity.
 *
 * <p>An establishment is optional on every row: a chamber membership belongs to the entity
 * as a whole, an establishment card does not, and forcing one would make somebody invent an
 * establishment to hold a document that is not about any of them.
 */
export function CompanyDocumentsPage() {
  useBreadcrumb()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const [chosen, setChosen] = useState('')

  // Derived, not synchronised. Copying the first entity into state inside an effect means a
  // second render before the page shows anything, and the state then has to be kept in step
  // with a list that can arrive late or change — which is a bug waiting for a slow network.
  const entityId = chosen || entities.data?.[0]?.id || ''

  const documents = useCompanyDocuments(entityId || undefined)
  const types = useDocumentTypes('COMPANY')
  const establishments = useEstablishments()
  const { save, remove } = useCompanyDocumentMutations()

  const [draft, setDraft] = useState<Draft | null>(null)
  const [toRemove, setToRemove] = useState<CompanyDocumentView | null>(null)
  const [error, setError] = useState<string | null>(null)

  const selectedType = types.data?.find((type) => type.id === draft?.documentTypeId)

  async function submit() {
    if (!draft) return
    setError(null)
    try {
      await save.mutateAsync({
        legalEntityId: entityId,
        establishmentId: draft.establishmentId || null,
        documentTypeId: draft.documentTypeId,
        documentNumber: draft.documentNumber || null,
        issuingAuthority: draft.issuingAuthority || null,
        issueDate: draft.issueDate || null,
        expiryDate: draft.expiryDate || null,
        storedFileId: draft.storedFileId,
        notes: draft.notes || null,
      })
      setDraft(null)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That document could not be saved')
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Company documents"
        subtitle="Trade licences, establishment cards and insurance — tracked and alerted on like anybody's passport."
        actions={
          <Button
            type="button"
            disabled={entityId === ''}
            onClick={() => { setError(null); setDraft({ ...EMPTY }) }}
          >
            <Plus className="size-4" aria-hidden="true" />
            Record a document
          </Button>
        }
      />

      <FilterBar>
        <Select
          id="company-entity"
          aria-label="Legal entity"
          value={entityId}
          onChange={(event) => setChosen(event.target.value)}
        >
          <option value="">Choose an entity…</option>
          {(entities.data ?? []).map((entity) => (
            <option key={entity.id} value={entity.id}>{entity.name}</option>
          ))}
        </Select>
      </FilterBar>

      <Card>
        {documents.isPending && entityId !== '' && <Skeleton className="m-4 h-24" />}
        {documents.isError && (
          <ErrorState error={documents.error} onRetry={() => void documents.refetch()} />
        )}
        {documents.isSuccess && documents.data.length === 0 && (
          <EmptyState
            title="Nothing on file for this entity"
            description="Record the trade licence so its expiry starts being tracked."
          />
        )}
        {(documents.data?.length ?? 0) > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full min-w-[48rem] text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                  <th scope="col" className="p-3 font-medium">Document</th>
                  <th scope="col" className="p-3 font-medium">Establishment</th>
                  <th scope="col" className="p-3 font-medium">Number</th>
                  <th scope="col" className="p-3 font-medium">Expires</th>
                  <th scope="col" className="p-3 font-medium">Status</th>
                  <th scope="col" className="p-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {documents.data?.map((document) => (
                  <tr key={document.id} className="border-b border-[var(--color-border)] last:border-0">
                    <td className="p-3 font-medium text-[var(--color-heading)]">
                      {document.documentTypeName}
                    </td>
                    <td className="p-3 text-[var(--color-muted)]">
                      {establishments.data?.find((e) => e.id === document.establishmentId)?.name
                        ?? 'The entity as a whole'}
                    </td>
                    <td className="p-3"><code className="text-xs">{document.documentNumber ?? '—'}</code></td>
                    <td className="p-3 tabular-nums">{document.expiryDate ?? '—'}</td>
                    <td className="p-3">
                      <DocumentStatusBadge
                        status={document.status}
                        daysUntilExpiry={document.daysUntilExpiry}
                        withPhrase
                      />
                    </td>
                    <td className="p-3 text-right">
                      <Button
                        type="button" size="icon" variant="ghost"
                        aria-label={`Remove ${document.documentTypeName}`}
                        onClick={() => setToRemove(document)}
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => !open && setDraft(null)}
        title="Record a company document"
        description="Recording a renewal supersedes the one already on file for that establishment."
        loading={save.isPending}
        error={error}
        onSubmit={() => void submit()}
        size="lg"
      >
        {draft && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="company-doc-type">Document</Label>
              <Select
                id="company-doc-type" required value={draft.documentTypeId}
                onChange={(event) => setDraft({ ...draft, documentTypeId: event.target.value })}
                className="w-full"
              >
                <option value="">Choose a document type…</option>
                {(types.data ?? []).filter((type) => type.active).map((type) => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="company-doc-establishment">Establishment</Label>
              <Select
                id="company-doc-establishment" value={draft.establishmentId}
                onChange={(event) => setDraft({ ...draft, establishmentId: event.target.value })}
                className="w-full"
              >
                <option value="">The entity as a whole</option>
                {(establishments.data ?? []).map((establishment) => (
                  <option key={establishment.id} value={establishment.id}>{establishment.name}</option>
                ))}
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="company-doc-number">
                Number{selectedType?.requiresNumber ? '' : ' (optional)'}
              </Label>
              <Input
                id="company-doc-number" required={selectedType?.requiresNumber}
                value={draft.documentNumber}
                onChange={(event) => setDraft({ ...draft, documentNumber: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="company-doc-expiry">
                Expires{selectedType?.requiresExpiry ? '' : ' (optional)'}
              </Label>
              <DatePicker
                id="company-doc-expiry" required={selectedType?.requiresExpiry}
                value={draft.expiryDate}
                onChange={(event) => setDraft({ ...draft, expiryDate: event.target.value })}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="company-doc-authority">Issuing authority</Label>
              <Input
                id="company-doc-authority" value={draft.issuingAuthority}
                onChange={(event) => setDraft({ ...draft, issuingAuthority: event.target.value })}
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Scan{selectedType?.requiresFile ? '' : ' (optional)'}</Label>
              <FileUploader
                ownerType="company_document"
                ownerId={entityId}
                onUploaded={(file: UploadedFile) => setDraft({ ...draft, storedFileId: file.id })}
                onRemoved={() => setDraft({ ...draft, storedFileId: null })}
              />
            </div>
          </div>
        )}
      </FormDialog>

      <ConfirmDialog
        open={toRemove != null}
        onOpenChange={(open) => !open && setToRemove(null)}
        title={`Remove ${toRemove?.documentTypeName ?? 'this document'}?`}
        description="The scan is kept. The document simply stops counting as on file and stops alerting."
        confirmLabel="Remove"
        onConfirm={() => {
          if (toRemove) {
            remove.mutate(toRemove.id, { onSettled: () => setToRemove(null) })
          }
        }}
      />
    </div>
  )
}
